import React from 'react';
import { useDeviceStore, useStores } from '../../../../store';
import {
    TextFieldItem,
    CheckboxGroupItem,
    SimpleFileUploadItem,
    IconTextButton,
    notification,
} from 'ui-lib';

import { getErrorMessage } from 'common-utils';
import { confirmAction } from '../../admin/utils';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import { useSimpleFileUploadProps } from 'ui-lib/components/forms/items/use-simple-file-upload-item';
import { ISendCommandSubmitFormValues, SendCommandForm } from './send-command-form';
import fileTransferCss from './file-transfer.css';

interface IFileTransferStateProps {
    uid?: string;
    deviceGroupName?: string;
}

export const FileTransfer = observer(() => {
    const devicesStore = useDeviceStore();
    const stores = useStores();

    const state: Partial<IFileTransferStateProps> = useLocation().state || {};
    const [destination, setFolderPath] = React.useState('');
    const permissionsOptions = [
        { value: 1, label: 'Execute' },
        { value: 2, label: 'Write' },
        { value: 4, label: 'Read' },
    ];
    const [checkedList, setCheckedList] = React.useState<CheckboxValueType[]>([4]);

    const MAX_SIZE_FILE_TRANSFER = 22; // Kilobytes
    const simpleFileUploadProps = useSimpleFileUploadProps({
        limitFileSize: MAX_SIZE_FILE_TRANSFER,
        allowedFileTypes: ['text/plain', '.conf', '.css', '.xml', '.json', '.jpg', '.png', '.list', '.pem', '.key', '.ini'],
        multiple: true,
        uploadButton: { label: 'Select files', type: 'primary' },
    });

    const _notificationInfos = (nodes: React.ReactNode[]) => {
        if (nodes.length == 0) return;

        notification['info']({
            message: <div>{...nodes}</div>,
            placement: 'topRight',
            duration: 10,
        });
    };

    const transferFile = async (options: ISendCommandSubmitFormValues) => {
        try {
            const { varOrClientAccountId, deviceGroupName, deviceName, enableReferenceLink } = options;
            const permissionsCode = checkedList.reduce((p: number, c) => p + Number(c), 0);
            const permissionsCodeLinux = `${permissionsCode}${permissionsCode}${permissionsCode}`;
            const result = await devicesStore.dataTransfer(
                varOrClientAccountId,
                deviceName || '',
                deviceGroupName || '',
                destination,
                permissionsCodeLinux,
                simpleFileUploadProps.files,
            );

            simpleFileUploadProps.setFiles([]);
            const successMessages = result.success?.map((item) => {
                return item ? (
                    <p>
                        {'Success to transfer file: '}
                        <strong>{`${item.fileName}`}</strong>.
                    </p>
                ) : null;
            });

            const errorMessages = result.error?.map((item) => {
                return item ? (
                    <p>
                        {'Failed to transfer file: '}
                        <strong>{`${item.fileName}`}</strong>.
                    </p>
                ) : null;
            });

            enableReferenceLink(result.deviceUids[0]);

            _notificationInfos(successMessages);
            _notificationInfos(errorMessages);
        } catch (err) {
            notification['error']({
                message: getErrorMessage(err),
                placement: 'topRight',
            });
        }
    };

    return (
        <SendCommandForm
            onSubmit={async (options: ISendCommandSubmitFormValues) => {
                confirmAction(undefined, 'Transfer File', 'Do you want to transfer this file?', () =>
                    transferFile(options),
                );
                return false;
            }}
            deviceGroupName={state.deviceGroupName}
            uid={state.uid}
            extraFormItems={[
                <TextFieldItem
                    initialValue={destination}
                    key='folderPath'
                    code='folderPath'
                    label='Folder path:'
                    isRequired={true}
                    labelAlign='right'
                    onChange={(value) => setFolderPath(value.target.value)}
                />,
                <CheckboxGroupItem
                    labelAlign='right'
                    key='permissions'
                    code='permissions'
                    defaultValue={checkedList}
                    label='Permissions:'
                    options={permissionsOptions}
                    value={checkedList}
                    onChange={(value) => setCheckedList(value)}
                />,
                <SimpleFileUploadItem
                    key='fileUpload'
                    simpleFileUploadProps={simpleFileUploadProps.getFileUploadProps()}
                    code='fileUpload'
                    label=' '
                />,
                <IconTextButton
                    key='transfer-submit-button'
                    className={fileTransferCss.button}
                    htmlType='submit'
                    label='Transfer'
                    disabled={!simpleFileUploadProps.files.length || simpleFileUploadProps.hasFileError}
                />,
            ]}
        />
    );
});
